import { useTranslation } from 'react-i18next';

import { IClinic } from '../hooks/useClinics';

import { AscendPartnerLevel } from './AscendPartnerLevel';

import '../i18n';
const BASE_URL = import.meta.env.VITE_BASE_URL;

type Props = {
  clinic: IClinic;
  id?: string;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  distance?: string;
  className?: string;
  separator?: boolean;
};

function handleDataLayer(clinic: IClinic, event: string) {
  // @ts-expect-error
  if (window.dataLayer) {
    // @ts-expect-error
    window.dataLayer.push({
      'clinic_ID': clinic.Id,
      'clinic_Name': clinic.Name,
      'event': event,
    });
  }
}

export function ClinicCard({ clinic, id, onClick = () => {}, distance, className = '' }: Props) {
  const { t } = useTranslation();

  const drName =
    clinic.AccountContactRelations &&
    `${clinic.AccountContactRelations.records[0]?.Contact.Salutation} ${
      clinic.AccountContactRelations.records[0]?.Contact.Name
    }`;

  return (
    <div
      className={`flex w-full flex-col bg-clip-border px-[16px] pb-[20px] text-primary ${className}`}
      id={id}
      onClick={onClick}
    >
      <div className="flex items-center justify-between text-[0.875em] font-light">
        <span>{distance && distance}</span>
      </div>
      <div className="mt-[12px] grid grid-cols-[1fr_112px] gap-8 pb-[27px] h-[160px]">
        <div className="flex flex-col text-left rtl:text-right">
          <h2 className="mb-[12px] text-[1.125em] font-medium tracking-[-0.0675em]">
            {clinic.Name}
          </h2>

          <span className="mb-[8px] text-[0.875em] font-light">
            {drName && drName.replace('null ', '')}
          </span>
          <span className="text-[0.875em] font-light">{clinic.BillingStreet}</span>
          <span className="text-[0.875em] font-light">{clinic.BillingCity}</span>
          <span className="text-[0.875em] font-light">
            {clinic.BillingPostalCode ? `${clinic.BillingPostalCode}, ` : ''}{' '}
            {clinic.BillingCountry}
          </span>
        </div>

        {clinic.partnerLevel > 0 ? (
          <span className="flex-basis-[112px] aspect-square h-[112px] w-[112px] object-cover">
            <AscendPartnerLevel
              partnerLevel={clinic.partnerLevel}
              partnerLevelTitle={clinic.partnerLevelTitle}
            />
          </span>
        ) : (
          <img
            src={clinic.ClinicImageUrl__c || `${BASE_URL}/assets/images/clinic-placeholder.jpeg`}
            alt="clinic"
            className="flex-basis-[112px] aspect-square h-[112px] w-[112px] object-cover"
          />
        )}
      </div>
      <div className="clinic-card-meta mb-[7px] mt-auto flex justify-between text-[0.875em] flex-col md:flex-row gap-5">
        <div className="flex items-center gap-1">
          {clinic.Phone && (
            <a
              className="no-underline hover:cursor-pointer bg-primary text-white text-[0.875em] px-[24px] py-[16px] uppercase w-full text-center"
              href={`tel:${clinic.Phone}`}
              onClick={() => {
                handleDataLayer(clinic, 'Clinic Call');
              }}
            >
              {t('Call')} {clinic.Phone}
            </a>
          )}
        </div>
        <div className="flex items-center gap-1 mx-auto md:mx-0">
          <img
            src={`${BASE_URL}/assets/images/icons/card-marker.svg`}
            alt="map marker icon"
            className="h-[18px] w-[18px]"
          />
          <a
            target="_blank"
            className="clinic-directions"
            href={`http://maps.google.com/maps?q=${clinic.Geolocation__c.latitude},${clinic.Geolocation__c.longitude}`}
            onClick={() => {
              handleDataLayer(clinic, 'Clinic Directions');
            }}
            rel="noreferrer"
          >
            {t('Get directions')}
          </a>
        </div>
      </div>
    </div>
  );
}
